export const compactDetail = (...values) => values.find((value) => value && String(value).trim()) || '';

export const buildCurrentDocument = (record) => {
  if (!record?.file_token) return null;

  return {
    file_token: record.file_token,
    referral_slug: record.referral_entity_slug,
    send_token: record.send_token,
    receiver_type: record.receiver_type,
    title: compactDetail(record.document_title, record.document_category, 'Current document'),
    referrer: compactDetail(record.referring_provider, record.sender_name, record.caller_name, 'Unknown referrer'),
    referrer_detail: compactDetail(record.provider_office, record.sender_email, record.sender_fax),
    to: compactDetail(record.receiver_name, 'Medsender'),
    to_detail: compactDetail(record.receiver_email, record.receiver_fax, record.receiver),
    num_pages: record.num_pages,
    created_at: compactDetail(record.received_at, record.created_at, record.date_received),
    is_current: true,
    is_primary: record.is_primary_referral_record === true,
  };
};

const documentTime = (document) => {
  const parsed = new Date(document?.created_at).getTime();
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
};

export const earliestDocument = (documents) => (
  documents.reduce((earliest, document) => {
    if (!earliest) return document;

    return documentTime(document) < documentTime(earliest) ? document : earliest;
  }, null)
);

export const selectedTokenSet = (tokens) => new Set(tokens.filter(Boolean));

const uniqueSlugs = (documents) => (
  [...new Set(documents.map((document) => document.referral_slug).filter(Boolean))]
);

const uniqueTokens = (tokens) => [...new Set(tokens.filter(Boolean))];

const documentGroupTokens = (document, documents) => {
  if (!document?.referral_slug) return [document?.file_token].filter(Boolean);

  return documents
    .filter((candidate) => candidate.referral_slug === document.referral_slug)
    .map((candidate) => candidate.file_token)
    .filter(Boolean);
};

const defaultSelectedTokens = (documents) => {
  const sourceTokens = documents
    .filter((document) => document.is_source_referral || document.is_current)
    .map((document) => document.file_token)
    .filter(Boolean);
  const targetReferralSlugs = uniqueSlugs(documents.filter((document) => !document.is_source_referral));
  const targetTokens = targetReferralSlugs.length === 1
    ? documents
      .filter((document) => document.referral_slug === targetReferralSlugs[0])
      .map((document) => document.file_token)
      .filter(Boolean)
    : [];

  return uniqueTokens([...sourceTokens, ...targetTokens]);
};

export const CURRENT_BUCKET_KEY = '__current__';

export const buildDocumentGroups = (documents) => {
  const matchedSlugs = new Set(
    documents.filter((document) => !document.is_current && document.referral_slug).map((document) => document.referral_slug),
  );
  const groupsByKey = new Map();

  documents.forEach((document) => {
    const joinsExisting = Boolean(document.referral_slug) && matchedSlugs.has(document.referral_slug);
    const isCurrentBucket = Boolean(document.is_current) && !joinsExisting;
    const key = isCurrentBucket ? CURRENT_BUCKET_KEY : document.referral_slug;

    if (!groupsByKey.has(key)) {
      groupsByKey.set(key, {
        key,
        isCurrentBucket,
        referralSlug: isCurrentBucket ? null : document.referral_slug,
        status: isCurrentBucket ? null : document.referral_status,
        recordCount: isCurrentBucket ? 0 : document.referral_record_count || 0,
        documents: [],
      });
    }
    groupsByKey.get(key).documents.push(document);
  });

  return [...groupsByKey.values()]
    .map((group) => {
      const recordCount = group.isCurrentBucket
        ? group.documents.length
        : group.recordCount || group.documents.length;
      const isExisting = !group.isCurrentBucket && Boolean(group.referralSlug);

      return {
        ...group,
        recordCount,
        isExisting,
        isAlreadyLinked: isExisting && recordCount > 1,
        documents: [...group.documents].sort((a, b) => documentTime(a) - documentTime(b)),
      };
    })
    .sort((a, b) => Number(a.isCurrentBucket) - Number(b.isCurrentBucket));
};

const buildSourceDocuments = (record, sourceReferralEntity) => {
  if (!sourceReferralEntity?.records?.length) return [];

  return sourceReferralEntity.records.map((document) => ({
    ...document,
    referral_slug: sourceReferralEntity.slug,
    referral_status: sourceReferralEntity.current_status,
    referral_record_count: sourceReferralEntity.record_count,
    is_current: document.is_current || document.file_token === record?.file_token,
    is_source_referral: true,
  }));
};

export const buildReviewDocuments = (record, matches, sourceReferralEntity = null) => {
  const sourceDocuments = buildSourceDocuments(record, sourceReferralEntity);
  const currentDocument = buildCurrentDocument(record);
  const currentDocumentFallback = currentDocument && sourceDocuments.length === 0
    ? { ...currentDocument, referral_slug: null, is_primary: false }
    : currentDocument;
  const matchedDocuments = matches.flatMap((match) => (
    (match.referral_entity?.records || []).map((document) => ({
      ...document,
      referral_slug: match.referral_entity?.slug,
      referral_status: match.referral_entity?.current_status,
      referral_record_count: match.referral_entity?.record_count,
    }))
  ));

  return [
    ...(sourceDocuments.length > 0 ? sourceDocuments : [currentDocumentFallback].filter(Boolean)),
    ...matchedDocuments,
  ];
};

const shouldSelectNewDefault = (token, documents, preservedSet, seenTokenSet) => {
  const document = documents.find((candidate) => candidate.file_token === token);
  if (!document?.referral_slug) return true;

  const groupTokens = documentGroupTokens(document, documents);
  const groupWasSeen = groupTokens.some((groupToken) => seenTokenSet.has(groupToken));
  const groupHasSelection = groupTokens.some((groupToken) => preservedSet.has(groupToken));

  return !groupWasSeen || groupHasSelection;
};

export const initialReviewSelectionState = {
  selectedRecordTokens: [],
  seenTokens: [],
};

export const reviewSelectionReducer = (state, action) => {
  switch (action.type) {
    case 'documentsChanged': {
      const documents = action.documents || [];
      const knownTokens = documents.map((document) => document.file_token).filter(Boolean);
      const knownTokenSet = new Set(knownTokens);
      const preserved = state.selectedRecordTokens.filter((token) => knownTokenSet.has(token));
      const preservedSet = new Set(preserved);
      const seenTokenSet = new Set(state.seenTokens);
      const newDefaults = defaultSelectedTokens(documents).filter((token) => (
        !preservedSet.has(token)
          && !seenTokenSet.has(token)
          && shouldSelectNewDefault(token, documents, preservedSet, seenTokenSet)
      ));
      const selectedRecordTokens = [...preserved, ...newDefaults];

      return {
        selectedRecordTokens,
        seenTokens: uniqueTokens([...state.seenTokens, ...knownTokens]),
      };
    }
    case 'toggleDocument': {
      const documents = action.documents || [];
      const document = documents.find((candidate) => candidate.file_token === action.fileToken);
      const toggledTokens = documentGroupTokens(document, documents);
      const toggledTokenSet = new Set(toggledTokens);
      const selectedTokenSetForToggle = selectedTokenSet(state.selectedRecordTokens);
      const allToggledTokensSelected = toggledTokens.every((token) => selectedTokenSetForToggle.has(token));
      const selectedRecordTokens = allToggledTokensSelected
        ? state.selectedRecordTokens.filter((token) => !toggledTokenSet.has(token))
        : uniqueTokens([...state.selectedRecordTokens, ...toggledTokens]);

      return {
        ...state,
        selectedRecordTokens,
      };
    }
    case 'pruneStaleTokens': {
      const staleTokenSet = new Set((action.staleTokens || []).filter(Boolean));
      const selectedRecordTokens = state.selectedRecordTokens.filter((token) => !staleTokenSet.has(token));

      return {
        ...state,
        selectedRecordTokens,
      };
    }
    default:
      return state;
  }
};

export const buildReferralReviewViewModel = ({
  record,
  matches,
  sourceReferralEntity,
  selectedRecordTokens,
}) => {
  const documents = buildReviewDocuments(record, matches, sourceReferralEntity);
  const documentGroups = buildDocumentGroups(documents);
  const selectedRecords = selectedTokenSet(selectedRecordTokens);
  const selectedDocuments = documents.filter((document) => selectedRecords.has(document.file_token));
  const selectedReferralSlugs = uniqueSlugs(selectedDocuments);
  const primaryDocument = earliestDocument(selectedDocuments);
  const sourceReferralSlug = sourceReferralEntity?.slug || null;
  const selectedTargetReferralSlugs = selectedReferralSlugs.filter((slug) => slug && slug !== sourceReferralSlug);
  const targetReferralSlug = selectedTargetReferralSlugs.includes(primaryDocument?.referral_slug)
    ? primaryDocument.referral_slug
    : selectedTargetReferralSlugs[0];
  const primaryMatch = matches.find((match) => match.referral_entity?.slug === targetReferralSlug);
  const primaryReferral = primaryMatch?.referral_entity;
  const reviewedPrimaryRecordToken = primaryReferral?.records?.find((document) => document.is_primary)?.file_token || null;
  const currentDocument = documents.find((document) => document.is_current);
  const destinationRecord = primaryDocument || currentDocument;
  const currentRecordSelected = Boolean(currentDocument?.file_token && selectedRecords.has(currentDocument.file_token));
  const currentRecordIsStandalone = currentRecordSelected && !sourceReferralSlug;
  const extraSelectedReferralSlugs = selectedTargetReferralSlugs.filter((slug) => slug && slug !== targetReferralSlug);
  const hasUnsupportedReviewPlan = extraSelectedReferralSlugs.length > 0;
  const hasLinkSource = sourceReferralSlug ? sourceReferralSlug !== targetReferralSlug : currentRecordIsStandalone;
  const selectedDocumentTokens = selectedDocuments.map((document) => document.file_token);
  const selectedDocumentTokenSet = selectedTokenSet(selectedDocumentTokens);
  const requestedPrimaryRecordToken = primaryDocument?.file_token || null;
  const primaryRecordSelected = Boolean(
    requestedPrimaryRecordToken && selectedDocumentTokenSet.has(requestedPrimaryRecordToken),
  );
  const canLink = Boolean(
    currentRecordSelected
      && targetReferralSlug
      && hasLinkSource
      && primaryRecordSelected
      && !hasUnsupportedReviewPlan,
  );

  return {
    documents,
    documentGroups,
    selectedRecords,
    primaryDocument,
    primaryReferral,
    destinationRecord,
    targetReferralSlug,
    selectedDocumentTokens,
    requestedPrimaryRecordToken,
    reviewedPrimaryRecordToken,
    hasUnsupportedReviewPlan,
    canLink,
    patientName: compactDetail(record?.patient_name, matches[0]?.referral_entity?.patient_name, 'Unknown patient'),
    patientDOB: compactDetail(record?.patient_dob, matches[0]?.referral_entity?.patient_dob),
  };
};
